import { TextInput } from "@jugl-web/ui-components";
import { cx, useFormFieldsValidators } from "@jugl-web/utils";
import parsePhoneNumber, {
  CountryCode,
  getCountryCallingCode,
} from "libphonenumber-js";
import { CSSProperties, FC, HTMLInputTypeAttribute } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { ChatButton } from "../../../ChatButton";
import { ChatBotPhoneInput } from "../ChatBotPhoneInput/ChatBotPhoneInput";

export interface ChatBotInputsFormProps {
  inputs: {
    name: string;
    type: HTMLInputTypeAttribute;
    isRequired?: boolean;
  }[];
  defaultCountryCode: CountryCode;
  className?: string;
  style?: CSSProperties;
  onSubmit: (data: { name: string; value: string }[]) => void;
}

interface FormFieldValue {
  value: string;
  countryCode?: CountryCode;
}

interface FormValues {
  fields: FormFieldValue[];
}

// Normalize phone number to E.164 (country code + digits only)
// so equivalent inputs identify the same lead on the backend
const normalizePhoneNumber = (phoneNumberValue: string): string =>
  parsePhoneNumber(phoneNumberValue)?.format("E.164") ?? phoneNumberValue;

export const ChatBotInputsForm: FC<ChatBotInputsFormProps> = ({
  inputs,
  defaultCountryCode,
  className,
  style,
  onSubmit,
}) => {
  const { control, formState, register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      fields: inputs.map((input) => ({
        value: "",
        ...(input.type === "tel" ? { countryCode: defaultCountryCode } : {}),
      })),
    },
  });

  const { fields } = useFieldArray({ control, name: "fields" });

  const { requiredValidator, emailValidator } = useFormFieldsValidators();

  const handleFormSubmit = handleSubmit((data) => {
    const transformedData = data.fields.map((field, index) => {
      const inputDef = inputs[index];

      let { value } = field;

      if (inputDef.type === "tel" && field.value) {
        const countryCode = field.countryCode ?? defaultCountryCode;

        value = normalizePhoneNumber(
          `+${getCountryCallingCode(countryCode)}${field.value}`
        );
      }

      return {
        name: inputs[index].name,
        value,
      };
    });

    onSubmit(transformedData);
  });

  return (
    <form
      className={cx("flex w-full flex-col gap-4", className)}
      style={style}
      onSubmit={handleFormSubmit}
      noValidate
    >
      <div className="flex w-full flex-col gap-4">
        {fields.map((field, index) => {
          const inputDef = inputs[index];
          const fieldError = formState.errors.fields?.[index]?.value;
          const isPhoneField = inputDef.type === "tel";

          if (isPhoneField) {
            return (
              <ChatBotPhoneInput
                key={field.id}
                inputDef={inputDef}
                fieldIndex={index}
                control={control}
                defaultCountryCode={defaultCountryCode}
                errorMessage={fieldError?.message}
              />
            );
          }

          return (
            <TextInput
              key={field.id}
              type={inputDef.type}
              isRequired={inputDef.isRequired}
              labelPosition="border-overlap"
              size="default"
              placeholder="Enter"
              label={inputDef.name}
              isInvalid={!!fieldError}
              errorMessage={fieldError?.message}
              {...register(`fields.${index}.value`, {
                ...(inputDef.isRequired && requiredValidator()),
                ...(inputDef.type === "email" && emailValidator()),
              })}
            />
          );
        })}
      </div>
      <ChatButton type="submit" variant="primary" size="md" fullWidth>
        Send
      </ChatButton>
    </form>
  );
};
